'use client';

import * as DS from '@aurorasolar/ds';
import { x } from '@xstyled/styled-components';
import { useRouter, useSearchParams } from 'next/navigation';
import { useContext, useEffect, useRef, useState } from 'react';

import { useTracking } from '@/analytics';
import { PostSummary } from '@/app/home-solar/blog/actions';
import { BlogSlideshow } from '@/app/home-solar/blog/components';
import { familyEvHomeSolar } from '@/app/lp/assets';
import { BannerWide, D3, GetStartedCta, Section, Subscribe } from '@/app/lp/components';
import { VisitorContext } from '@/context';
import { TRACKING } from '@/helpers/tracking';

import {
  BILL_MAX,
  BILL_MIN,
  BillChart,
  FaqSection,
  HeroInputs,
  MethodologySection,
  QuoteCta,
  RefineDetails,
  ResultsSummary,
  SolutionToggles,
} from './components';
import {
  DEFAULT_HEATING_FUEL_BY_REGION,
  HeatingFuel,
  resolveRegion,
  ResolvedRegion,
  runSimulation,
  SimulationResult,
  SimulationToggles,
} from './engine';

const DEFAULT_BILL = 200;
const URL_SYNC_DEBOUNCE_MS = 750;

// heatingFuel is null until the user picks one — it then defaults to the
// region's typical fuel (see effectiveHeatingFuel below).
type RefinementsState = { heatingFuel: HeatingFuel | null; homeSqft: number };

type CalculatorState = {
  bill: number;
  refinements: RefinementsState;
  solutions: SimulationToggles;
  zip: string;
};

const DEFAULT_STATE: CalculatorState = {
  bill: DEFAULT_BILL,
  refinements: { heatingFuel: null, homeSqft: 1800 },
  solutions: {
    battery: false,
    batteryMode: 'SELF_USE',
    heatPump: false,
    insulation: false,
    smartHome: false,
    solar: false,
  },
  zip: '',
};

const clampBill = (bill: number): number => Math.min(Math.max(bill, BILL_MIN), BILL_MAX);

const buildResult = (state: CalculatorState, region: ResolvedRegion, heatingFuel: HeatingFuel): SimulationResult => {
  // A homeowner who already heats with a heat pump cannot add one.
  const heatPump = state.solutions.heatPump && heatingFuel !== 'HEAT_PUMP' && heatingFuel !== 'NONE';

  return runSimulation({
    climateRegion: region.climateRegion,
    heatingFuel,
    homeSqft: state.refinements.homeSqft,
    monthlyBillUsd: state.bill,
    stateCode: region.stateCode,
    toggles: { ...state.solutions, heatPump },
  });
};

type Props = { slideshowPosts?: PostSummary[] };

export const ElectrificationCalculatorPage = ({ slideshowPosts = [] }: Props) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { trackEvent } = useTracking();
  const { visitor } = useContext(VisitorContext);

  const visitorZip = visitor?.address?.zip ?? visitor?.zip ?? visitor?.geolocation?.zip;
  const paramZip = searchParams.get('zip');
  const paramBill = Number(searchParams.get('bill'));

  const [state, setState] = useState<CalculatorState>({
    ...DEFAULT_STATE,
    bill: paramBill > 0 ? clampBill(paramBill) : DEFAULT_BILL,
    zip: paramZip ?? visitorZip ?? '',
  });

  // Keep zip/bill deep-linkable without spamming history on slider drags.
  // Skip the initial mount: state is seeded from the URL/defaults, so firing a
  // replace or an analytics event before any user interaction is just noise.
  const syncTimer = useRef(0);
  const hasInteracted = useRef(false);
  useEffect(() => {
    if (!hasInteracted.current) {
      hasInteracted.current = true;
      return;
    }

    window.clearTimeout(syncTimer.current);
    syncTimer.current = window.setTimeout(() => {
      const params = new URLSearchParams(searchParams.toString());
      if (state.zip.length === 5) {
        params.set('zip', state.zip);
      }
      params.set('bill', String(state.bill));
      router.replace(`?${params.toString()}`, { scroll: false });
      trackEvent(TRACKING.ELECTRIFICATION_UPDATED_INPUTS, { bill: state.bill, zip: state.zip });
    }, URL_SYNC_DEBOUNCE_MS);

    return () => window.clearTimeout(syncTimer.current);
    // searchParams/trackEvent are stable across input changes; syncing on them would loop the replace
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.bill, state.zip]);

  const region = resolveRegion(state.zip);
  // Default to the region's typical heating fuel until the user overrides it.
  const effectiveHeatingFuel: HeatingFuel =
    state.refinements.heatingFuel ?? (region ? DEFAULT_HEATING_FUEL_BY_REGION[region.climateRegion] : 'GAS');
  const result = region ? buildResult(state, region, effectiveHeatingFuel) : undefined;
  const stateCode = region?.stateCode;
  const hasPlan = Object.values(state.solutions).some((value) => value === true);
  const zipError = state.zip.length === 5 && !result ? 'We couldn’t find that zip code — try another.' : undefined;

  const updateSolutions = (patch: Partial<SimulationToggles>) => {
    Object.entries(patch).forEach(([solution, enabled]) => {
      if (typeof enabled === 'boolean') {
        trackEvent(TRACKING.ELECTRIFICATION_TOGGLED_SOLUTION, { enabled, solution });
      }
    });
    setState((current) => ({ ...current, solutions: { ...current.solutions, ...patch } }));
  };

  return (
    <article>
      {/* Compact hero: keeps the chart near the fold on mobile */}
      <DS.Flex as="header" gap={4} maxW="640px" mx="auto" pt={{ _: 8, md: 10 }} px={6} textAlign="center">
        <x.h1 text={{ _: 'h2', md: 'd1' }}>
          <span>Electrify your home. </span>
          <span>Simulate your savings.</span>
        </x.h1>
        <DS.Text color="uiHelperCopy" text={{ _: 'body16', md: 'body20' }}>
          Start with your zip code and monthly electric bill, then add solar, batteries, and more to see your costs
          change in real time.
        </DS.Text>
        <HeroInputs
          bill={state.bill}
          onBillChange={(bill) => setState((current) => ({ ...current, bill }))}
          onZipChange={(zip) => setState((current) => ({ ...current, zip }))}
          zip={state.zip}
          zipError={zipError}
        />
      </DS.Flex>

      <Section align="center">
        <DS.Flex gap={5} maxW="900px" mx="auto" w="100%">
          <DS.Text as="h2" text="h3">
            Your simulated energy costs
          </DS.Text>
          {result ? (
            <BillChart baseline={result.baselineMonths} scenario={hasPlan ? result.scenarioMonths : undefined} />
          ) : (
            <DS.Text color="uiHelperCopy" text="body16">
              Enter your zip code above to start your simulation.
            </DS.Text>
          )}
          <ResultsSummary result={hasPlan ? result : undefined} />

          <DS.Flex gap={2} pt={4} w="100%">
            <DS.Text as="h2" text="h3">
              Build your energy plan
            </DS.Text>
            <DS.Text color="uiHelperCopy" pb={2} text="body12">
              <span>Toggle solutions on to see how they change your costs.</span>
            </DS.Text>
            <SolutionToggles heatingFuel={effectiveHeatingFuel} onChange={updateSolutions} toggles={state.solutions} />
          </DS.Flex>

          <RefineDetails
            batteryMode={state.solutions.batteryMode}
            onBatteryModeChange={(batteryMode) => updateSolutions({ batteryMode })}
            onChange={(patch) =>
              setState((current) => ({ ...current, refinements: { ...current.refinements, ...patch } }))
            }
            refinements={{ heatingFuel: effectiveHeatingFuel, homeSqft: state.refinements.homeSqft }}
            solarEnabled={state.solutions.solar}
          />
        </DS.Flex>
      </Section>

      <QuoteCta zip={state.zip} />

      <Section>
        <MethodologySection result={result} stateCode={stateCode} />
      </Section>

      <Section>
        <FaqSection />
      </Section>

      <BannerWide bgImage={familyEvHomeSolar}>
        <D3>Customers save an average of $120* per month by adding solar to their homes</D3>
        <p>*Based on national averages, real prices and savings may vary.</p>
      </BannerWide>

      {slideshowPosts.length > 0 ? (
        <BlogSlideshow posts={slideshowPosts} title="Explore solar guides and insights" />
      ) : null}

      <GetStartedCta ctaLabel="Get estimates">
        <Subscribe buttonText="Subscribe" variant="primary" />
      </GetStartedCta>
    </article>
  );
};
